import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Badge} from 'primereact/badge'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {ReactComponent as ArrowRight} from '../../../assets/icons/ArrowRight.svg'
import {SystemRole} from '../../../Constants'
import {useAppConfig} from '../../../context/app/AppContext'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {useAuth} from '../../authentication/AuthProvider'
import {getPageHospitals} from '../../hospital/HospitalEndPoint'
import {
    useHospitalCRUD,
    usePageHospitals,
} from '../../hospital/query-keys/HospitalQueryManager'
import PageContentWrapper from '../../layout/PageContentWrapper'
import {useRoleContext} from '../../role/RoleProvider'
import {useImportNotificationContext} from '../../table-utils/importForm/ImportNotificationProvider'
import {
    filterDataTableEvent,
    filterEventTypes,
    getFilterEventKey,
    removeFilterChangeListener,
} from '../broadcast/FilterEvent'
import {
    exportColumnTypes,
    exportRecordTypes,
    exportTypes,
    tableToolbarType,
} from '../Constant'
import {DatatableContext} from '../DataTableContext'
import StyledDataTableWrapper from '../StyledDataTableWrapper'
import {
    getAdvancedFilter,
    getShouldRememberAdvancedFilter,
    tableTypes,
} from '../StyledTableUtils'
import HospitalForm from './HospitalForm'
import {hospitalColumnList} from './HospitalTableConstants'

const StyledHospitalDatatable = () => {
    useSignals()

    const queryClient = useQueryClient()

    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const {hospitalManagementPermissions} = useRoleContext()
    const {handleOnExportDatatable} = useImportNotificationContext()

    //#endregion

    //#region Load Data from Local Storage
    const initialAdvancedFilterRequest = useMemo(() => {
        const didRememberSettings = getShouldRememberAdvancedFilter(
            tableTypes.hospital,
        )

        if (didRememberSettings) {
            const storedFilterRequest = getAdvancedFilter(tableTypes.hospital)
            if (
                !!storedFilterRequest &&
                Object.keys(storedFilterRequest).length > 0
            ) {
                return storedFilterRequest
            }
        }

        return {}
    }, [])
    //#endregion

    //#region State Controls
    const pageable = useSignal({
        pageRows: 10,
        pageNumber: 0,
        sortField: 'createdAt',
        sortOrder: -1,
    })

    const filterRequest = useSignal({
        globalSearchValue: null,
        groupByIsNull: true,
        ...initialAdvancedFilterRequest,
    })

    // Pageable
    const handleChangeSortColumn = useCallback(
        e => {
            pageable.value = {
                ...pageable.value,
                sortField: e.sortField,
                sortOrder: e.sortOrder,
            }
        },
        [pageable.value.sortField, pageable.value.sortOrder],
    )

    const handleChangePageNumber = useCallback(
        newPageNumber => {
            pageable.value = {
                ...pageable.value,
                pageNumber: newPageNumber,
            }

            handlePrefetchDataByPage(newPageNumber + 1)
        },
        [pageable.value.pageNumber],
    )

    const handleChangePageRows = useCallback(
        newPageRows => {
            pageable.value = {
                ...pageable.value,
                pageRows: newPageRows,
                pageNumber: 0,
            }

            pagePrefetchRef.current = [0]

            handlePrefetchDataByPage(1)
        },
        [pageable.value.pageRows],
    )

    // Global Search Value
    const handleChangeGlobalSearch = useCallback(value => {
        filterRequest.value = {
            ...filterRequest.value,
            globalSearchValue: value || null,
        }
    }, [])

    // Filter Request
    const handleClearFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: null,
            groupByIsNull: true,
        }
    }, [])

    const handleClearFilterRequestByKey = useCallback((key, value) => {
        filterRequest.value = {
            ...filterRequest.value,
            [key]: value,
        }
    }, [])

    const handleClearAdvancedFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: filterRequest.value?.globalSearchValue || null,
            groupByIsNull: true,
        }
    }, [])

    // Table Action CRUD
    const formRef = useRef(null)

    const {mutateAsync: deleteHospital} = useHospitalCRUD(
        QueryCRUDTypes.delete,
        user?.id,
    )

    const handleAddNew = useCallback(() => {
        formRef.current?.showForm()
    }, [])
    //#endregion

    //#region Check Permission
    const handleCheckPermissionByKey = useCallback(
        key => {
            if (!key) {
                return false
            }

            if (
                userRole === SystemRole.ADMIN ||
                hospitalManagementPermissions?.includes(key)
            ) {
                return true
            }

            return false
        },
        [hospitalManagementPermissions, userRole],
    )
    //#endregion

    //#region Data Controls
    const getCurrentQueryKey = ({newPageNumber, newPageRows}) => {
        const {hospitalKeys} = QueryKeyManager(QueryKeyTypes.hospital, user?.id)

        return hospitalKeys.page({
            pageNumber:
                newPageNumber !== null && typeof newPageNumber === 'number'
                    ? newPageNumber
                    : pageable.value.pageNumber,
            pageRows:
                newPageRows !== null && typeof newPageNumber === 'number'
                    ? newPageRows
                    : pageable.value.pageRows,
            sortField: pageable.value.sortField,
            sortOrder: pageable.value.sortOrder,
            filterRequest: {
                ...filterRequest.value,
            },
        })
    }

    const {data: queryData, isFetched} = usePageHospitals(
        user?.id,
        userRole,
        pageable.value.pageNumber,
        pageable.value.pageRows,
        pageable.value.sortField,
        pageable.value.sortOrder,
        filterRequest.value,
        true,
    )

    const dataList = useRef([])
    const currentDataList = useMemo(() => {
        dataList.current = queryData?.content || []

        return queryData?.content || []
    }, [queryData?.content])

    // Page Rows from Pagination
    const rowsOptionHoveredRef = useRef([10])
    const handlePrefetchDataByRowsOption = useCallback(rows => {
        if (!!user?.id && !rowsOptionHoveredRef.current?.includes(rows)) {
            rowsOptionHoveredRef.current.push(rows)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: 0,
                    newPageRows: rows,
                }),
                queryFn: () =>
                    getPageHospitals(
                        user?.id,
                        0,
                        rows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])

    const handleOnDetailRowClick = id => {
        if (!!id) {
            const selectedRowData = dataList.current?.find(row => row.id === id)
            if (!!selectedRowData) {
                formRef.current?.showForm(selectedRowData)
            }
        }
    }

    // Page Number from Pagination
    const pagePrefetchRef = useRef([0])
    const handlePrefetchDataByPage = useCallback(pageNumber => {
        if (!!user?.id && !pagePrefetchRef.current?.includes(pageNumber)) {
            pagePrefetchRef.current.push(pageNumber)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: pageNumber,
                    newPageRows: pageable.peek().pageRows,
                }),
                queryFn: () =>
                    getPageHospitals(
                        user?.id,
                        pageNumber,
                        pageable.peek().pageRows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])

    //#region Event Listener on State Changes
    useEffect(() => {
        handlePrefetchDataByPage(pageable.value.pageNumber + 1)

        filterDataTableEvent.on(
            `${getFilterEventKey(
                tableTypes.hospital,
                filterEventTypes.advanced,
            )}`,
            newFilterConfig => {
                if (!newFilterConfig || typeof newFilterConfig !== 'object') {
                    return
                }

                if (Object.keys(newFilterConfig).length === 0) {
                    handleClearAdvancedFilterRequest()

                    return
                }

                filterRequest.value = {
                    ...filterRequest.value,
                    ...newFilterConfig,
                }
            },
        )

        return () => {
            removeFilterChangeListener(
                tableTypes.hospital,
                filterEventTypes.advanced,
            )
        }
    }, [])
    //#endregion

    //#region Export Data
    const handleRetrieveExportData = async (
        {
            exportType = exportTypes.excel, // csv, excel, pdf
            exportRecordType = exportRecordTypes.allRecords, // all, currentInView
            exportColumns = exportColumnTypes.allColumns, // all, currentInView
        },
        columns,
    ) => {
        handleOnExportDatatable(
            {
                exportType: exportType,
                exportRecordType: exportRecordType,
                exportColumns: exportColumns,
            },
            queryData,
            pageable.value,
            filterRequest.value,
            columns,
            tableTypes.hospital,
        )
    }
    //#endregion

    //#region Expansion Row
    const rowExpansionTemplate = useCallback(rowData => {
        return (
            <div className="col-12">
                <div className="mb-3">
                    <h6
                        className="underline-60 font-bold"
                        style={{fontSize: '1.2em'}}>
                        {rowData?.name}'s Sub Hospitals
                    </h6>
                    <p>
                        The following hospitals{' '}
                        <span className="font-bold text-darkcyan">
                            ({rowData?.children?.length} in total)
                        </span>{' '}
                        are affiliated with{' '}
                        <span className="font-bold text-darkcyan">
                            {rowData?.name}
                        </span>
                        .
                    </p>
                </div>

                {rowData?.children?.map((child, index) => {
                    return (
                        <div key={index} className="col-12 cursor-pointer ">
                            <hr className="p-0 m-0" />
                            <div className="flex align-items-center justify-content-around  hover:bg-white py-2 ">
                                <div className="mx-3">
                                    <Badge value={index + 1}></Badge>
                                </div>
                                <div className="w-full grid">
                                    <div className="col">
                                        <h6 className="text-gray-500 font-bold m-0 mb-1 ">
                                            Hospital Number
                                        </h6>
                                        <p>{child?.hospitalNumber}</p>
                                    </div>
                                    <div className="col">
                                        <h6 className="text-gray-500 font-bold m-0 mb-1 ">
                                            Hospital Name
                                        </h6>
                                        <div className="flex align-items-center">
                                            {child?.logo ? (
                                                <div className="mr-2">
                                                    <img
                                                        alt="logo"
                                                        src={child?.logo}
                                                        style={{
                                                            width: 24,
                                                            height: 24,
                                                        }}
                                                    />
                                                </div>
                                            ) : null}
                                            <div>{child?.name}</div>
                                        </div>
                                    </div>
                                    <div className="col">
                                        <h6 className="text-gray-500 font-bold m-0 mb-1 ">
                                            Description
                                        </h6>
                                        <p>{child?.description || 'N/A'}</p>
                                    </div>
                                    <div className="col">
                                        <h6 className="text-gray-500 font-bold m-0 mb-1 ">
                                            No. of HC.Practitioners
                                        </h6>
                                        <p>{child?.numberOfHCP} HCP in total</p>
                                    </div>
                                    <div className="col">
                                        <h6 className="text-gray-500 font-bold m-0 mb-1 ">
                                            No. of Patients
                                        </h6>
                                        <p>
                                            {child?.numberOfPatients} Patients
                                            in total
                                        </p>
                                    </div>
                                    <div className="col">
                                        <h6 className="text-gray-500 font-bold m-0 mb-1 ">
                                            Created At
                                        </h6>
                                        <p>{child?.createdAt || 'N/A'}</p>
                                    </div>
                                </div>
                                <div
                                    className="mx-3"
                                    onClick={() =>
                                        formRef.current?.showForm(child)
                                    }>
                                    <ArrowRight width={22} height={22} />
                                </div>
                            </div>
                            <hr className="p-0 m-0" />
                        </div>
                    )
                })}
            </div>
        )
    }, [])
    //#endregion Expansion Row

    return (
        <PageContentWrapper>
            <DatatableContext.Provider
                value={{
                    dataKey: 'id',
                    tableType: tableTypes.hospital,
                    queryData: queryData,
                    isFetched: isFetched,
                    columnList: hospitalColumnList,
                    tableToolbar: [tableToolbarType.tableControlToolbar],

                    expansionOption: {
                        hasExpansion: true,
                        expansionKey: 'children',
                        rowExpansionTemplate: rowExpansionTemplate,
                    },

                    pageable: pageable.value,
                    onChangeSortColumn: handleChangeSortColumn,
                    onChangePageNumber: handleChangePageNumber,
                    onChangePageRows: handleChangePageRows,
                    onPageRowsHovered: handlePrefetchDataByRowsOption,

                    onDetailRowClick: handleOnDetailRowClick,
                    handleCheckPermissionByKey: handleCheckPermissionByKey,

                    initialAdvancedFilterRequest: initialAdvancedFilterRequest,
                    handleClearFilterRequest: handleClearFilterRequest,
                    handleClearFilterRequestByKey:
                        handleClearFilterRequestByKey,
                    handleClearAdvancedFilterRequest:
                        handleClearAdvancedFilterRequest,

                    handleRetrieveExportData: handleRetrieveExportData,
                    handleChangeGlobalSearch: handleChangeGlobalSearch,

                    handleAddNew: handleAddNew,
                }}>
                <StyledDataTableWrapper
                    headerOptions={{
                        title: 'Hospital Management',
                        subTitle:
                            'Hospital Management Board and its affiliated hospitals table brings convenience and efficiency in organizing healthcare management information',
                    }}
                />
                <HospitalForm ref={formRef} />
            </DatatableContext.Provider>
        </PageContentWrapper>
    )
}

export default StyledHospitalDatatable
